import type { BrokerSearchResult, ProviderOffer } from "../../../packages/providers/src/index.js";
import { DISCOVERY_CATEGORIES, type DiscoveryCategory } from "../../../packages/domain/src/index.js";
import { createOfferActionModel } from "./explore-state.js";

export const EXPLORE_PRIMARY_CATEGORIES = Object.freeze([
  "destinations", "nearby_places", "stays", "flights", "cruises", "tours", "activities", "events",
  "restaurants", "local_guides", "lounges", "day_rooms", "sleep_pods", "luggage", "transfers", "offers"
] as const);

export const EXPLORE_VIEW_MODES = Object.freeze(["map", "list"] as const);
export const EXPLORE_SEARCH_CATEGORIES: readonly DiscoveryCategory[] = DISCOVERY_CATEGORIES;
export const FAMILY_FILTER_CATEGORIES: readonly DiscoveryCategory[] = Object.freeze(["stays", "restaurants", "tours", "activities"]);

export interface ExploreSurfaceInput {
  readonly query: string;
  readonly selectedCategory: DiscoveryCategory;
  readonly result: BrokerSearchResult;
  readonly viewMode: "map" | "list";
  readonly familyFriendlyOnly: boolean;
}

export interface ExploreCapabilitySummary {
  readonly category: string;
  readonly actionable: boolean;
  readonly providers: readonly string[];
}

function isDiscoveryCategory(category: string): category is DiscoveryCategory {
  return (DISCOVERY_CATEGORIES as readonly string[]).includes(category);
}

function isActionableCapability(capability: ExploreCapabilitySummary): boolean {
  return capability.actionable === true && capability.providers.length > 0;
}

export function deriveExploreSearchCategories(capabilities: readonly ExploreCapabilitySummary[]): readonly DiscoveryCategory[] {
  const actionable = capabilities
    .filter(isActionableCapability)
    .map(({ category }) => category)
    .filter(isDiscoveryCategory);
  return Object.freeze([...new Set(actionable)]);
}

export function resolveExploreSearchCategory(capabilities: readonly ExploreCapabilitySummary[], selected: DiscoveryCategory): DiscoveryCategory {
  const categories = deriveExploreSearchCategories(capabilities);
  return categories.includes(selected) ? selected : categories[0] ?? selected;
}

export function resolveExploreSearchAvailability(capabilities: readonly ExploreCapabilitySummary[], category: string) {
  const capability = capabilities.find((candidate) => candidate.category === category);
  const providers = capability?.providers ?? [];
  const available = capability?.actionable === true && providers.length > 0;
  return Object.freeze({
    available,
    providers: Object.freeze([...providers]),
    message: available
      ? "Search will show live results that are available for this request."
      : "Live search is not available yet for this category. Deals will appear when an offer source is ready."
  });
}

export function createExploreSurface(input: ExploreSurfaceInput) {
  if (input.query.trim().length === 0) throw new Error("Explore query must not be blank");
  if (input.result.category !== input.selectedCategory) throw new Error("Explore category must match provider results");
  const results = input.familyFriendlyOnly ? input.result.offers.filter((offer) => offer.familyFriendly === true) : input.result.offers;
  return Object.freeze({
    query: input.query,
    selectedCategory: input.selectedCategory,
    viewMode: input.viewMode,
    mapUsesProviderHandoff: true as const,
    bulkOfflineMapCaching: false as const,
    familyFriendlyOnly: input.familyFriendlyOnly,
    results: Object.freeze(results.map(createOfferCard)),
    unavailableProviders: input.result.unavailableProviders,
    emptyState: results.length === 0 ? "No results are available for this location yet." : null,
    fakeInventoryVisible: false as const,
    weatherAlertsEnabled: false as const,
    travelAlertsEnabled: false as const
  });
}

export function createOfferCard(offer: ProviderOffer) {
  const actions = createOfferActionModel(offer);
  return Object.freeze({
    id: offer.offerId,
    title: offer.title,
    summary: offer.summary,
    totalPriceMinor: offer.price?.totalMinor ?? null,
    currency: offer.price?.currency ?? null,
    taxesAndFeesIncluded: offer.price === null ? null : true,
    rating: offer.rating,
    distanceMeters: offer.distanceMeters,
    familyFriendly: offer.familyFriendly,
    locationLabel: offer.locationLabel,
    memberSavingsMinor: offer.memberSavings?.amountMinor ?? null,
    memberSavingsEvidence: offer.memberSavings?.evidenceReference ?? null,
    projectedPoints: offer.pointsEligible && offer.price !== null ? Math.floor(offer.price.totalMinor / 100) : 0,
    perks: offer.perks,
    cancellationTerms: offer.cancellationTerms,
    sponsoredLabel: offer.sponsored ? "Sponsored" as const : null,
    bestValueExplanation: offer.bestValueExplanation,
    providerAttribution: offer.provenance.attribution,
    sourceFreshUntil: offer.provenance.expiresAt,
    availableModes: offer.modes,
    actions,
    saveEnabled: true as const,
    shareEnabled: true as const
  });
}

export type GlobalSearchResultKind = "destination" | "airport" | "cruise_port" | "flight" | "hotel" | "cruise" | "tour" | "activity" | "offer" | "restaurant" | "local_guide" | "event" | "livestream" | "saved_item" | "trip";

export interface GlobalSearchInputResult {
  readonly category: string;
  readonly id: string;
  readonly title: string;
  readonly capabilityActionable: boolean;
  readonly provenance: string;
  readonly summary?: string;
  readonly detailKind?: GlobalSearchResultKind;
}

function detailKindForCategory(category: string): GlobalSearchResultKind {
  if (category === "stays" || category === "hotel") return "hotel";
  if (category === "flights" || category === "flight") return "flight";
  if (category === "cruises" || category === "cruise") return "cruise";
  if (category === "tours" || category === "tour") return "tour";
  if (category === "activities" || category === "activity") return "activity";
  if (category === "offers" || category === "offer") return "offer";
  if (category === "restaurants" || category === "restaurant") return "restaurant";
  if (category === "local_guides" || category === "local_guide") return "local_guide";
  if (category === "events" || category === "event") return "event";
  if (category === "livestreams" || category === "livestream") return "livestream";
  if (category === "trips" || category === "trip") return "trip";
  if (category === "airports" || category === "airport") return "airport";
  if (category === "cruise_ports" || category === "cruise_port") return "cruise_port";
  return "destination";
}

export function createGlobalSearchSurface(results: readonly GlobalSearchInputResult[]) {
  const modeled = results.map((result) => {
    const detailKind = result.detailKind ?? detailKindForCategory(result.category);
    return Object.freeze({
      ...result,
      detailKind,
      detailHref: result.capabilityActionable ? `/${detailKind}s/${encodeURIComponent(result.id)}` : null,
      status: result.capabilityActionable ? "available" as const : "unavailable" as const,
      statusText: result.capabilityActionable
        ? `Open ${detailKind.replaceAll("_", " ")} detail from ${result.provenance}.`
        : `${result.title} is hidden from actionable results because ${result.category} is not backed by an active verified provider.`,
      actions: Object.freeze({
        openDetail: result.capabilityActionable,
        save: result.capabilityActionable,
        share: result.capabilityActionable,
        continueWithProvider: false as const
      })
    });
  });
  return Object.freeze({
    categories: Object.freeze([...new Set(modeled.map((result) => result.category))]),
    results: Object.freeze(modeled.filter((result) => result.capabilityActionable)),
    unavailableResults: Object.freeze(modeled.filter((result) => !result.capabilityActionable)),
    detailModelsByCategory: Object.freeze([...new Set(modeled.map((result) => result.detailKind))]),
    unsupportedResultsHidden: results.some((result) => !result.capabilityActionable),
    naturalLanguageInterpretation: "AGENTIC_PHASE_05_TOOL_ONLY" as const,
    recentSearchesSupported: true as const,
    savedSearchesRequiredInV1: false as const
  });
}

export const DETAIL_PAGE_KINDS = Object.freeze(["destination", "airport", "cruise_port", "flight", "hotel", "cruise", "tour", "activity", "offer"] as const);

export function createLocalGuideCard(input: { readonly guideId: string; readonly bio: string; readonly languages: readonly string[]; readonly specialties: readonly string[]; readonly pricing: { readonly hourlyMinor: number | null; readonly halfDayMinor: number | null; readonly dayMinor: number | null; readonly currency: string }; readonly availabilitySummary: string; readonly cancellationTerms: string; readonly verifiedReviewCount: number; readonly capabilityActionable: boolean; readonly bookingMode: "booking" | "request" | "unavailable" }) {
  if (input.bio.trim().length === 0 || input.languages.length === 0 || input.cancellationTerms.trim().length === 0) throw new Error("Local guide cards require bio, languages, and cancellation terms");
  if (!input.capabilityActionable && input.bookingMode !== "unavailable") throw new Error("Unavailable guide capability cannot expose request or booking actions");
  return Object.freeze({ ...input, directBooking: input.capabilityActionable && input.bookingMode === "booking", requestEnabled: input.capabilityActionable && input.bookingMode === "request", saveEnabled: true as const, shareEnabled: true as const, portabilityTakeRateBasisPoints: 1_000 });
}
